/**
 * MentionAutocomplete
 *
 * Floating dropdown that appears when the user types `@` in a textarea.
 * Exposes a controlled interface: the parent owns the query string and
 * whether the dropdown is open; this component handles rendering + keyboard
 * navigation and calls onSelect when the user commits a choice.
 *
 * Keyboard nav: ↑/↓ to move, Enter to select, Esc to close.
 * The parent textarea must forward relevant keydown events via onKeyDown.
 */

import { useState, useEffect, useRef, useId } from 'react';
import type { MentionableUser } from '@/services/commentApi';

interface MentionAutocompleteProps {
  /** Whether the dropdown should be visible. */
  open: boolean;
  /** Current text after the `@` trigger (used to filter options). */
  query: string;
  /** Full list of mentionable users (already fetched by parent). */
  users: MentionableUser[];
  /** Called when the user selects a candidate. */
  onSelect: (user: MentionableUser) => void;
  /** Called when the user presses Esc or the list becomes empty. */
  onClose: () => void;
  /**
   * Exposes keyboard event handling to the parent textarea.
   * The parent should call this from its onKeyDown handler when `open` is true.
   * Returns true when the key was consumed (parent should preventDefault).
   */
  onKeyDown?: (e: React.KeyboardEvent) => boolean;
}

const MAX_RESULTS = 8;

function roleBadge(role: string | undefined): string | null {
  if (!role) return null;
  if (role === 'owner') return 'owner';
  if (role === 'coach' || role === 'channel_admin' || role === 'channel_coach') return 'coach';
  return null;
}

export default function MentionAutocomplete({
  open,
  query,
  users,
  onSelect,
  onClose,
  onKeyDown: exposeKeyDown,
}: MentionAutocompleteProps) {
  const listId = useId();
  const [activeIndex, setActiveIndex] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  // Filter users by query text (case-insensitive substring on display_name).
  const filtered = query
    ? users.filter((u) =>
        u.display_name.toLowerCase().includes(query.toLowerCase())
      ).slice(0, MAX_RESULTS)
    : users.slice(0, MAX_RESULTS);

  // Reset active index whenever query or visibility changes.
  useEffect(() => {
    setActiveIndex(0);
  }, [query, open]);

  // Scroll the active item into view.
  useEffect(() => {
    if (!listRef.current) return;
    const items = listRef.current.querySelectorAll('[role="option"]');
    const active = items[activeIndex] as HTMLElement | undefined;
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Close when filtered list becomes empty after typing.
  useEffect(() => {
    if (open && filtered.length === 0) {
      onClose();
    }
  }, [open, filtered.length, onClose]);

  // Keyboard handler — wired in from parent via the exposed ref pattern.
  const handleKeyDown = (e: React.KeyboardEvent): boolean => {
    if (!open || filtered.length === 0) return false;

    if (e.key === 'ArrowDown') {
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
      return true;
    }
    if (e.key === 'ArrowUp') {
      setActiveIndex((i) => Math.max(i - 1, 0));
      return true;
    }
    if (e.key === 'Enter') {
      const selected = filtered[activeIndex];
      if (selected) onSelect(selected);
      return true;
    }
    if (e.key === 'Escape') {
      onClose();
      return true;
    }
    return false;
  };

  // Expose the keyboard handler to parent so the textarea can forward events.
  // We use a stable ref-like approach — attach to the prop callback.
  if (exposeKeyDown) {
    // Replace inline each render; parent stores the ref from props.
    (exposeKeyDown as unknown as { _impl: typeof handleKeyDown })._impl = handleKeyDown;
  }

  if (!open || filtered.length === 0) return null;

  return (
    <ul
      ref={listRef}
      id={listId}
      role="listbox"
      aria-label="Mention suggestions"
      className="absolute z-50 bottom-full mb-1 left-0 right-0 max-h-48 overflow-y-auto
        rounded-lg border border-border-subtle bg-dark-elevated-2
        shadow-lg py-1"
    >
      {filtered.map((user, idx) => {
        const badge = roleBadge(user.role);
        const isActive = idx === activeIndex;
        return (
          <li
            key={user.user_id}
            role="option"
            aria-selected={isActive}
            onMouseEnter={() => setActiveIndex(idx)}
            onMouseDown={(e) => {
              // Prevent blur on the textarea before we can call onSelect.
              e.preventDefault();
              onSelect(user);
            }}
            className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm
              transition-colors
              ${isActive
                ? 'bg-accent/15 text-text-primary'
                : 'text-text-secondary hover:bg-dark-elevated-1'
              }`}
          >
            {/* Avatar placeholder */}
            <span
              aria-hidden="true"
              className="flex-shrink-0 w-6 h-6 rounded-full bg-accent/20 flex items-center justify-center
                text-[11px] font-semibold text-accent"
            >
              {user.display_name.charAt(0).toUpperCase()}
            </span>

            <span className="flex-1 truncate font-medium">
              {user.display_name}
            </span>

            {badge && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-info/20 text-info flex-shrink-0">
                {badge}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

